import { Dictionary, DistributiveOmit, IsAny, IsNever, MaybePromise, Optional } from '@direct-frontend/stdlib';
import { Reaction } from '@adv-frontend/reactions';

import {
    BaseFields,
    FieldInstance,
    IfFieldsRequireContextInput,
    InferFieldsContextInput,
    InferFieldsValue,
    ValidateFields,
} from '../fields/types';
import { BaseSubmitError, BaseValidationError } from '../types';

import { SubmitFormApi } from './formApi';
import { FormValidator } from './validation';
import { ReactionFormInstance } from './instanceHelpers';
import { FormState, FormStateGetters, FormSubscriptions } from '../store/types';

/** Ключ, за которым скрыты внутренние методы формы */
export const $internals = Symbol('$internals');
/** Ключ, за которым скрыты созданные инстансы полей формы */
export const $fields = Symbol('$fields');
/** Ключ, за которым скрыты созданные экшены формы */
export const $actions = Symbol('$actions');

/** Реакция формы */
export type FormReaction<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormContextInput,
    FormContext,
    FormSubmitArg,
    FormSubmitReturnValue,
> = Reaction<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- используем any для базового описания
    any,
    FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
>;

/** Объект с контекстом для синхронного экшена формы */
export type FormActionParams<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContext,
> = {
    draftValue: InferFieldsValue<FormFields>;
    getContext(): FormContext;
    getState(): FormState<InferFieldsValue<FormFields>, FormValidationError>;
};

/** Экшен формы. Принимает контекст и произвольный набор аргументов */
type FormAction<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContext,
    Args extends unknown[],
> = (
    context: FormActionParams<FormFields, FormValidationError, FormContext>,
    ...args: Args
) => void | undefined | InferFieldsValue<FormFields>;
/** Объект со всеми экшенами формы */
export type FormActionsMap<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = BaseValidationError,
    FormContext = unknown,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- используем any для универсального generic типа
    Args extends unknown[] = any[],
> = Dictionary<FormAction<FormFields, FormValidationError, FormContext, Args>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- используем any для generic типа
export type BaseFormActions = FormActionsMap<any>;

/** Публичный API действий формы, привязанный к инстансу формы */
export type BoundFormActions<FormActions> = IsAny<
    FormActions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- совместимость с any-инстансами форм
    any,
    BaseFormActions extends FormActions
        ? never
        : {
              // Отбрасываем первый аргумент каждого экшена (context), так как он передается автоматически
              [FormAction in keyof FormActions]: FormActions[FormAction] extends (
                  ...args: [infer _Context, ...infer Args]
              ) => infer ReturnValue
                  ? (...args: Args) => ReturnValue
                  : never;
          }
>;

/**
 * Асинхронный экшен формы. Принимает усечённый `formApi` (по аналогии с onSubmit/валидаторами,
 * а не полный инстанс формы), AbortSignal и произвольный набор аргументов.
 * Параметризуется кусочками формы — это разрывает циклическую зависимость с FormInstance.
 */
export type FormAsyncAction<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- произвольные аргументы экшена
    Args extends unknown[] = any[],
> = (
    formApi: AsyncActionsFormApi<FormFields, FormValidationError, FormContext, FormActions>,
    signal: AbortSignal,
    ...args: Args
) => Promise<void>;

/** Объект со всеми асинхронными экшенами формы (параметризуется кусочками, как FormActionsMap) */
export type FormAsyncActionsMap<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = BaseValidationError,
    FormContext = unknown,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext> = FormActionsMap<
        FormFields,
        FormValidationError,
        FormContext
    >,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- произвольные аргументы экшена
    Args extends unknown[] = any[],
> = Dictionary<FormAsyncAction<FormFields, FormValidationError, FormContext, FormActions, Args>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- базовый тип для любого объекта асинхронных экшенов
export type BaseFormAsyncActions = FormAsyncActionsMap<any, any, any, any>;

/**
 * Отбрасывает асинхронные экшены формы, если они не переданы и TypeScript вывел их тип в базовую мапу
 * (то есть в ограничение type-параметра). Без этого дефолтное значение `FormAsyncActions` раздувало бы
 * сериализуемый тип декларации формы (re-include `FormFields`/`FormActions`) и упирало в лимит TS7056.
 */
export type SkipUnknownFormAsyncActions<FormAsyncActions> =
    BaseFormAsyncActions extends NoInfer<FormAsyncActions> ? never : FormAsyncActions;

/** Публичный API асинхронных экшенов формы, привязанный к инстансу формы */
export type BoundFormAsyncActions<FormAsyncActions> = IsAny<
    FormAsyncActions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- совместимость с any-инстансами форм
    any,
    BaseFormAsyncActions extends FormAsyncActions
        ? Record<never, never>
        : {
              // Отбрасываем первые два аргумента (formApi и signal), так как они передаются автоматически
              [K in keyof FormAsyncActions]: FormAsyncActions[K] extends (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- базовые типы async action
                  ...args: [any, any, ...infer Args]
              ) => infer ReturnValue
                  ? (...args: Args) => ReturnValue
                  : never;
          }
>;

type ExtractReusableReactionFields<ReusableReaction> =
    ReusableReaction extends Reaction<infer _ReactionData, infer ReactionInstance>
        ? ReactionInstance extends ReactionFormInstance<infer RequiredFields>
            ? RequiredFields
            : never
        : never;

export type CompatibleReusableReaction<FormFields extends BaseFields, ReusableReaction> =
    ExtractReusableReactionFields<ReusableReaction> extends infer RequiredFields extends BaseFields
        ? keyof RequiredFields extends keyof FormFields
            ? Pick<FormFields, keyof RequiredFields> extends RequiredFields
                ? RequiredFields extends Pick<FormFields, keyof RequiredFields>
                    ? ReusableReaction
                    : never
                : never
            : never
        : never;

/** Создаёт список реакций формы. */
export type CreateReactionsApi<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormContextInput,
    FormContext,
    FormSubmitArg,
    FormSubmitReturnValue,
> = (
    reactions: FormReaction<
        FormName,
        FormFields,
        FormValidationError,
        FormActions,
        FormContextInput,
        FormContext,
        FormSubmitArg,
        FormSubmitReturnValue
    >[]
) => FormReaction<
    FormName,
    FormFields,
    FormValidationError,
    FormActions,
    FormContextInput,
    FormContext,
    FormSubmitArg,
    FormSubmitReturnValue
>[];

/**
 * Приводит переиспользуемую реакцию к реакции конкретной формы.
 *
 * На уровне типов проверяет, что все поля, требуемые реакцией через
 * {@link ReactionFormInstance}, присутствуют в форме и имеют те же типы.
 */
export type CreateReusableReactionApi<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormContextInput,
    FormContext,
    FormSubmitArg,
    FormSubmitReturnValue,
> = <ReusableReaction>(
    reaction: ReusableReaction &
        (ReusableReaction extends CompatibleReusableReaction<FormFields, ReusableReaction> ? unknown : never)
) => FormReaction<
    FormName,
    FormFields,
    FormValidationError,
    FormActions,
    FormContextInput,
    FormContext,
    FormSubmitArg,
    FormSubmitReturnValue
>;

/**
 * Параметры создания формы
 * @typeParam FormName Тип названия поля
 * @typeParam FormFields Тип всех полей формы
 * @typeParam FormSubmitReturnValue Тип возвращаемого значения метода `onSubmit`
 **/
export type FormOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<
        FormFields,
        FormValidationError,
        FormContext,
        FormActions
    > = FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions>,
> = {
    /** Название формы (используется в качестве названия модульного стора) */
    name: FormName;
    /** Объект с полями формы */
    fields: ValidateFields<FormFields>;

    /** Список валидаторов формы */
    validators?: (
        // Используем интерфейс с функцией, чтобы иметь возможность указать значение type-параметра руками
        createValidators: <PassedFormValidationError extends BaseValidationError>(
            validators?: FormValidators<FormFields, PassedFormValidationError, FormContext>
        ) => FormValidators<FormFields, PassedFormValidationError, FormContext>
    ) => FormValidators<FormFields, IsAny<FormValidationError, never, FormValidationError>, FormContext>;

    reactions?: (
        createReactions: CreateReactionsApi<
            FormName,
            FormFields,
            FormValidationError,
            NoInfer<FormActions>,
            FormContextInput,
            FormContext,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        createReusableReaction: CreateReusableReactionApi<
            FormName,
            FormFields,
            FormValidationError,
            NoInfer<FormActions>,
            FormContextInput,
            FormContext,
            FormSubmitArg,
            FormSubmitReturnValue
        >
    ) => FormReaction<
        FormName,
        FormFields,
        FormValidationError,
        NoInfer<FormActions>,
        FormContextInput,
        FormContext,
        FormSubmitArg,
        FormSubmitReturnValue
    >[];

    /** Экшены формы */
    actions?: FormActions;

    /** Асинхронные экшены формы */
    asyncActions?: (
        createAsyncActions: <
            T extends FormAsyncActionsMap<
                NoInfer<FormFields>,
                NoInfer<FormValidationError>,
                NoInfer<FormContext>,
                NoInfer<FormActions>
            >,
        >(
            asyncActions: T
        ) => T
    ) => FormAsyncActions;

    /**
     * Метод отправки формы
     * @params value Текущее значение полей формы
     * @params formApi Объект `formApi` для работы с формой
     **/
    onSubmit: (
        value: InferFieldsValue<NoInfer<FormFields>>,
        formApi: SubmitFormApi<NoInfer<FormFields>, NoInfer<FormValidationError>, FormContext, FormContextInput>,
        ...args: IsNever<FormSubmitArg, [], FormSubmitArg extends readonly unknown[] ? FormSubmitArg : [FormSubmitArg]>
    ) => MaybePromise<FormSubmitReturnValue>;

    /**
     * Метод для обработки сабмита невалидной формы
     * @params formApi Объект `formApi` для работы с формой
     * @default функция вызывающая `formApi.scrollToFirstError()`
     **/
    onSubmitFailed?: (
        formApi: SubmitFormApi<NoInfer<FormFields>, NoInfer<FormValidationError>, FormContext, FormContextInput>
    ) => MaybePromise<void>;
} & IfFieldsRequireContextInput<
    FormFields,
    {
        /** Функция, наполняющая контекст формы и её полей */
        context: (input: FormContextInput) => { form?: FormContext; fields: InferFieldsContextInput<FormFields> };
    },
    {
        /** Функция, наполняющая контекст формы */
        context?: (input: FormContextInput) => { form?: FormContext };
    }
>;

/**
 * Инстанс формы, передаваемый в async action колбэки.
 * Минимальный явный тип без циклических ссылок — только то что нужно пользователю:
 * поля, контекст, getValue, actions, addValidationError.
 * Аналог PrimitiveFieldInstanceWithoutAsyncActions для полей.
 */
export type AsyncActionsFormApi<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = never,
    FormContext = never,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext> = FormActionsMap<
        FormFields,
        FormValidationError,
        FormContext
    >,
> = {
    /** Объект с полями формы */
    fields: FieldsInstances<FormFields>;
    /** Возвращает текущее значение контекста формы */
    getContext: () => FormContext;
    /** Получает текущее значение всех полей формы */
    getValue: () => IsAny<FormFields, unknown, InferFieldsValue<FormFields>>;
    /** Получает текущее состояние формы */
    getState: () => FormState<InferFieldsValue<FormFields>, FormValidationError>;
    /**
     * Геттеры отдельных частей состояния формы
     *
     * Используйте `state` в реакциях и селекторах, а `getState()` — когда нужно всё состояние целиком
     *
     * @example
     * ```typescript
     * const isSubmitting = formApi.state.isSubmitting();
     * //    ^? boolean
     * ```
     */
    state: FormStateGetters<InferFieldsValue<FormFields>, FormValidationError>;
    /** Экшены формы */
    actions: BoundFormActions<FormActions>;
    /** Добавляет ошибку к форме */
    addValidationError: (error: DistributiveOmit<FormValidationError, 'meta'>) => void;
    /** Добавляет ошибки к форме */
    addValidationErrors: (errors: DistributiveOmit<FormValidationError, 'meta'>[]) => void;
};

/** Форма */
export type FormDeclaration<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions> = never,
> = {
    /** Название формы (может использоваться как дискриминант в юнионах) */
    name: FormName;
    /**
     * Возвращает инстанс формы, который позволяет работать со значением и состоянием формы
     * @param formId Идентификатор формы
     * @returns Инстанс формы
     **/
    getInstance: (
        formId: string
    ) => FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    >;
};

/** Выводит тип полей формы по её декларации */
export type InferFormFields<Declaration> =
    Declaration extends FormDeclaration<
        infer _,
        infer FormFields,
        infer __,
        infer ___,
        infer ____,
        infer _____,
        infer ______,
        infer _______,
        infer ________
    >
        ? FormFields
        : never;

/** Выводит тип полей формы по её декларации */
export type InferFormActions<Declaration> =
    Declaration extends FormDeclaration<
        infer _,
        infer __,
        infer ___,
        infer ____,
        infer _____,
        infer FormActions,
        infer ______,
        infer _______,
        infer ________
    >
        ? FormActions
        : never;

/**
 * Выводит тип значений формы по её декларации
 *
 * Можно использовать для проверки типов у объекта initialValue
 */
export type InferFormValue<Declaration> = InferFieldsValue<InferFormFields<Declaration>>;

/** Выводит тип инстанса формы по её декларации */
export type InferFormInstance<Declaration> = Declaration extends {
    getInstance: (formId: string) => infer I;
}
    ? I
    : never;

/** Объект с инстансами полей формы */
export type FieldsInstances<FormFields extends BaseFields> = {
    [FieldName in keyof FormFields & string]: FieldInstance<FormFields[FieldName], FieldName>;
};

/**
 * Урезанная версия инстанса формы
 * Используется в полях, чтобы избавиться от знания про все параметры родительской формы
 */
export type UnknownFormInstance = {
    /** Название формы */
    name: string;
    /** Идентификатор формы */
    id: string;

    /** Получает текущее состояние формы. Если форма не проинициализирована, то выбрасывает исключение */
    getState: () => FormState<unknown, BaseValidationError>;

    /**
     * Геттеры отдельных частей состояния формы
     *
     * Используйте `state` в реакциях и селекторах, а `getState()` — когда нужно всё состояние целиком
     *
     */
    state: FormStateGetters<unknown, BaseValidationError>;

    /** Создает подписку на состояние формы */
    subscribe: () => () => void;

    /**
     * @deprecated Метод является устаревшим и потенциально опасным. Если вы считаете, что его валидно использовать в вашем случае, приходите к @dislaty
     */
    isInitialized: () => boolean;

    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        /** Получает флаг, указывающий, что форма в данный момент сабмитится */
        useIsFormSubmitting: () => boolean;
        /** Хук, получает текущий композитный ключ инстанса формы (formId#uniquePart, генерируется при initialize) */
        useFormInitializationId: () => string | undefined;
        /** Текущий композитный id инициализации формы (formId#uniquePart, генерируется при initialize) */
        formInitializationId: string;
        /** Получает текущее состояние формы. Если форма не проинициализирована, то выбрасывает исключение */
        useState: () => FormState<unknown, BaseValidationError>;
    };
};

/** Объект со всеми валидаторами формы */
type FormValidators<FormFields extends BaseFields, FormValidationError extends BaseValidationError, FormContext> = {
    onChange?: FormValidator<FormFields, FormValidationError, FormContext>[];
    onSubmit?: FormValidator<FormFields, FormValidationError, FormContext>[];
};

/**
 * Инстанс формы (получается из вызова функции `form.getInstance`)
 * @typeParam FormName Название формы
 * @typeParam FormFields Тип всех полей формы
 * @typeParam FormSubmitReturnValue Тип возвращаемого значения метода `onSubmit`
 */
export type FormInstance<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = never,
    FormContextInput = never,
    FormContext = never,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext> = FormActionsMap<
        FormFields,
        FormValidationError,
        FormContext
    >,
    FormSubmitArg = never,
    FormSubmitReturnValue = unknown,
    FormAsyncActions extends FormAsyncActionsMap<
        FormFields,
        FormValidationError,
        FormContext,
        FormActions
    > = FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions>,
> = {
    /** Название формы (может использоваться как дискриминант в юнионах) */
    name: FormName;
    /** Идентификатор формы */
    id: string;
    /** Экшены формы */
    actions: BoundFormActions<FormActions>;
    /** Объект с экшенами формы, используется для кеширования результата геттера */
    [$actions]?: BoundFormActions<FormActions>;
    /** Асинхронные экшены формы */
    asyncActions: BoundFormAsyncActions<FormAsyncActions>;
    /** Получает текущее значение всех полей формы. Если форма не проинициализирована, то выбрасывает исключение */
    getValue: () => IsAny<FormFields, unknown, InferFieldsValue<FormFields>>;
    /** Получает текущее состояние формы. Если форма не проинициализирована, то выбрасывает исключение */
    getState: () => FormState<InferFieldsValue<FormFields>, FormValidationError>;
    /**
     * Геттеры отдельных частей состояния формы
     *
     * Используйте `state` в реакциях и селекторах, а `getState()` — когда нужно всё состояние целиком
     *
     * @example
     * ```typescript
     * const isSubmitting = form.getInstance('form-id').state.isSubmitting();
     * //    ^? boolean
     * ```
     */
    state: FormStateGetters<InferFieldsValue<FormFields>, FormValidationError>;
    /** Возвращает текущее значение контекста формы */
    getContext: () => FormContext;
    /**
     * Устанавливает новое значение контекста формы и всех её полей
     * @deprecated Метод является устаревшим и потенциально опасным. Используйте только реактивный контекст компонента Form
     */
    setContext: (input: FormContextInput) => void;

    /** Объект с полями формы */
    fields: FieldsInstances<FormFields>;
    /** Объект с полями формы, используется для кеширования результата геттера */
    [$fields]?: FieldsInstances<FormFields>;

    /** Запускает повторную валидацию формы и всех её дочерних полей */
    revalidate: () => void;

    /** Сбрасывает состояние формы до значения с которым она была проинициализирована */
    reset: () => void;

    /**
     * Сбрасывает форму и инициализирует новым значением
     *
     * @param formValue - значение формы. Если не передано, то используются значения по умолчанию из полей
     * @param context - Используйте только реактивный контекст компонента Form
     */
    reinitialize: (
        formValue?: InferFieldsValue<FormFields>,
        /** @deprecated Используйте только реактивный контекст компонента Form */
        contextArg?: FormContextInput
    ) => FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    >;

    /** Выполняет отправку формы */
    submit: (
        ...submitArgs: IsNever<
            FormSubmitArg,
            [],
            FormSubmitArg extends readonly unknown[] ? FormSubmitArg : [FormSubmitArg]
        >
    ) => Promise<Optional<FormSubmitReturnValue>>;

    /** Добавляет ошибку к форме */
    addValidationError: (error: DistributiveOmit<FormValidationError, 'meta'>) => void;
    /** Добавляет ошибки к форме */
    addValidationErrors: (errors: DistributiveOmit<FormValidationError, 'meta'>[]) => void;

    /**
     * Добавляет ошибку, которая будет возвращена в onSubmit
     * @param error - ошибка
     */
    addSubmitError: (error: BaseSubmitError) => void;

    /**
     * @deprecated Метод является устаревшим и потенциально опасным. Если вы считаете, что его валидно использовать в вашем случае, приходите к @dislaty
     */
    isInitialized: () => boolean;

    /**
     * Создает подписку на состояние формы
     * @returns Функция для отписки
     **/
    subscribe: () => () => void;

    /** Прокручивает страницу до первого поля с ошибкой */
    scrollToFirstError: () => void;

    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        /** Объект с декларациями полей формы */
        fields: FormFields;
        /** Список валидаторов формы */
        readonly validators?: FormValidators<
            FormFields,
            IsAny<FormValidationError, never, FormValidationError>,
            FormContext
        >;
        /** Список реакций формы */
        readonly reactions?: FormReaction<
            FormName,
            FormFields,
            FormValidationError,
            FormActions,
            FormContextInput,
            FormContext,
            FormSubmitArg,
            FormSubmitReturnValue
        >[];

        /** Текущий композитный ключ инстанса формы (formId#uniquePart, генерируется при initialize) */
        readonly formInitializationId: string;

        /** Флаг, указывающий, что initializationId зафиксирован в текущем инстансе */
        readonly isFrozenInitializationId: boolean;

        /** Признак наличия серверного снапшота формы из `__REDUX_INITIAL_STATE__` */
        readonly isHydratedFromServer: boolean;

        /** Хук, получает текущий композитный ключ инстанса формы (formId#uniquePart, генерируется при initialize) */
        useFormInitializationId: () => string;

        /**
         * Фиксирует initializationId в текущем инстансе.
         * Такие инстансы используются для запуска/остановки асинхронных операций (реакций, валидаций),
         * чтобы они не переключались на новый initializationId при реинициализации формы.
         *
         * Для избежания проблем должен быть вызван сразу после создания инстанса.
         * @param initializationId - ID инициализации, который нужно зафиксировать
         */
        freezeInitializationId: (initializationId: string) => void;

        /** Проверяет проинициализирована ли форма */
        useIsFormInitialized: () => boolean;

        /** Получает текущее значение всех полей формы. Если форма не проинициализирована, то выбрасывает исключение */
        useValue: () => InferFieldsValue<FormFields>;

        /** Получает текущее состояние формы. Если форма не проинициализирована, то выбрасывает исключение */
        useState: () => FormState<InferFieldsValue<FormFields>, FormValidationError>;
        /** Получает флаг, указывающий, что форма в данный момент сабмитится */
        useIsFormSubmitting: () => boolean;

        /** Получает текущее значение контекста формы. Если форма не проиницализирована, то выбрасывает исключение */
        useContext: () => FormContext;

        /** Получает текущие подписки на данные формы */
        getSubscriptions: () => FormSubscriptions;

        /**
         * Инициализирует форму с новым уникальным ID
         * При каждом вызове создается новый formInitializationId и запускаются реакции
         * @param formValue Значение, которым должна быть проинициализирована форма. Если не передано, то используются
         * значения по умолчанию из полей
         * @param context Значение для инициализации контекста формы и её полей
         **/
        initialize: IsNever<
            FormContextInput,
            (
                formValue?: InferFieldsValue<FormFields>,
                context?: FormContextInput,
                options?: { shouldReinitialize?: boolean }
            ) => void,
            (
                formValue: InferFieldsValue<FormFields> | undefined,
                context: FormContextInput,
                options?: { shouldReinitialize?: boolean }
            ) => void
        >;

        /**
         * Записывает начальное состояние формы в стор – первая половина `initialize`, без запуска реакций.
         * Без `shouldReinitialize` идемпотентна: повторный вызов на уже проинициализированной форме
         * ничего не делает. С `shouldReinitialize: true` идемпотентности нет – каждый вызов заводит
         * новый `formInitializationId`. Вызывать во время рендеринга можно только тогда, когда это
         * разрешил `canInitializeDuringRender`.
         */
        initializeState: IsNever<
            FormContextInput,
            (
                formValue?: InferFieldsValue<FormFields>,
                context?: FormContextInput,
                options?: { shouldReinitialize?: boolean }
            ) => void,
            (
                formValue: InferFieldsValue<FormFields> | undefined,
                context: FormContextInput,
                options?: { shouldReinitialize?: boolean }
            ) => void
        >;

        /** Запускает реакции текущей инициализации формы – вторая половина `initialize` */
        startReactions: () => void;

        /**
         * Можно ли записать начальное состояние формы прямо во время рендеринга: форма еще не
         * проинициализирована и не деинициализируется – значит, `initializeState` отработает
         * идемпотентно и React может повторить рендер.
         */
        canInitializeDuringRender: () => boolean;

        /**
         * Внутренний метод для реинициализации формы
         */
        reinitialize: (
            formValue?: InferFieldsValue<FormFields>,
            contextArg?: FormContextInput
        ) => FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue,
            FormAsyncActions
        >;

        /** Очищает данные формы */
        cleanup: () => void;
    };
};
